/**
 * RFC 9457 problem details as rendered by BillerAPI's ProblemRenderer:
 * {type, title, status, detail, instance, code, request_id, ...extensions}.
 */
export interface ProblemDetails {
  type?: string;
  title: string;
  status: number;
  detail: string;
  instance?: string;
  code: string;
  request_id: string | null;
  errors?: Record<string, string[]>;
  [extension: string]: unknown;
}

/** Stable codes the UI branches on (BillerAPI `App\Exceptions\ErrorCode`). */
export const ErrorCode = {
  ValidationFailed: 'VALIDATION_FAILED',
  BadRequest: 'BAD_REQUEST',
  Unauthenticated: 'UNAUTHENTICATED',
  Forbidden: 'FORBIDDEN',
  ResourceNotFound: 'RESOURCE_NOT_FOUND',
  RouteNotFound: 'ROUTE_NOT_FOUND',
  MethodNotAllowed: 'METHOD_NOT_ALLOWED',
  RateLimited: 'RATE_LIMITED',
  IdempotencyKeyRequired: 'IDEMPOTENCY_KEY_REQUIRED',
  IdempotencyKeyReused: 'IDEMPOTENCY_KEY_REUSED',
  IdempotencyRequestInProgress: 'IDEMPOTENCY_REQUEST_IN_PROGRESS',
  PreconditionFailed: 'PRECONDITION_FAILED',
  VersionConflict: 'VERSION_CONFLICT',
  InternalError: 'INTERNAL_ERROR',
  InvalidCredentials: 'INVALID_CREDENTIALS',
  MembershipRequired: 'MEMBERSHIP_REQUIRED',
  PublicLinkInvalid: 'PUBLIC_LINK_INVALID',
  QuoteNotEditable: 'QUOTE_NOT_EDITABLE',
  QuoteNotSendable: 'QUOTE_NOT_SENDABLE',
  QuoteNotAcceptable: 'QUOTE_NOT_ACCEPTABLE',
  QuoteExpired: 'QUOTE_EXPIRED',
  ContractNotEditable: 'CONTRACT_NOT_EDITABLE',
  ContractNotExecutable: 'CONTRACT_NOT_EXECUTABLE',
  ContractNotActive: 'CONTRACT_NOT_ACTIVE',
  AmendmentNotExecutable: 'AMENDMENT_NOT_EXECUTABLE',
  AmendmentLineUnknown: 'AMENDMENT_LINE_UNKNOWN',
  PaymentMethodRequired: 'PAYMENT_METHOD_REQUIRED',
  SubscriptionNotChangeable: 'SUBSCRIPTION_NOT_CHANGEABLE',
  SubscriptionContractManaged: 'SUBSCRIPTION_CONTRACT_MANAGED',
  ScheduleOverlap: 'SCHEDULE_OVERLAP',
  EffectiveDateOutOfRange: 'EFFECTIVE_DATE_OUT_OF_RANGE',
  // Client-side only:
  NetworkError: 'NETWORK_ERROR',
  InvalidResponse: 'INVALID_RESPONSE',
} as const;

export type KnownErrorCode = (typeof ErrorCode)[keyof typeof ErrorCode];

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly title: string;
  readonly detail: string;
  readonly requestId: string | null;
  readonly fieldErrors: Readonly<Record<string, string[]>>;
  readonly problem: ProblemDetails | null;
  readonly method: string;
  readonly path: string;

  constructor(init: {
    status: number;
    code: string;
    title: string;
    detail: string;
    requestId?: string | null;
    fieldErrors?: Record<string, string[]>;
    problem?: ProblemDetails | null;
    method: string;
    path: string;
    cause?: unknown;
  }) {
    super(init.detail || init.title, init.cause === undefined ? undefined : { cause: init.cause });
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.title = init.title;
    this.detail = init.detail;
    this.requestId = init.requestId ?? null;
    this.fieldErrors = init.fieldErrors ?? {};
    this.problem = init.problem ?? null;
    this.method = init.method;
    this.path = init.path;
  }

  /** The endpoint is not deployed yet (404 ROUTE_NOT_FOUND). */
  get isRouteMissing(): boolean {
    return this.code === ErrorCode.RouteNotFound || this.code === ErrorCode.MethodNotAllowed;
  }

  get isNotFound(): boolean {
    return this.code === ErrorCode.ResourceNotFound;
  }

  get isValidation(): boolean {
    return this.status === 422 && this.code === ErrorCode.ValidationFailed;
  }

  get isNetwork(): boolean {
    return this.code === ErrorCode.NetworkError;
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  /**
   * Whether the outcome of a mutation is unknown, so retrying with the same
   * Idempotency-Key is the right move (network failure, 5xx, in-flight,
   * rate limit). Any other answer is final for that key.
   */
  get isRetryableWithSameKey(): boolean {
    return (
      this.isNetwork ||
      this.status >= 500 ||
      this.status === 429 ||
      this.code === ErrorCode.IdempotencyRequestInProgress ||
      this.code === ErrorCode.VersionConflict
    );
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/** Human copy for an error, used by toasts and inline alerts. */
export function describeError(error: unknown): { title: string; detail: string; requestId: string | null } {
  if (isApiError(error)) {
    if (error.isRouteMissing) {
      return {
        title: 'Not available yet',
        detail: `The API does not serve ${error.method} ${error.path} yet.`,
        requestId: error.requestId,
      };
    }
    if (error.isNetwork) {
      return { title: 'Cannot reach the API', detail: error.detail, requestId: null };
    }
    return { title: error.title, detail: error.detail, requestId: error.requestId };
  }
  if (error instanceof Error)
    return { title: 'Something went wrong', detail: error.message, requestId: null };
  return { title: 'Something went wrong', detail: String(error), requestId: null };
}
